import type React from 'react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import { clsx } from 'clsx';
import { X } from 'lucide-react';

import styles from './MobileDrawer.module.scss';

type DrawerAccent = 'student' | 'teacher' | 'guest';

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  accent?: DrawerAccent;
  children: React.ReactNode;
}

export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  open,
  onClose,
  title = 'Меню',
  accent = 'student',
  children,
}) => {
  const titleId = useId();
  const drawerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (open) {
      return;
    }

    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      drawerRef.current?.contains(active)
    ) {
      active.blur();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <>
      <button
        type='button'
        className={clsx(styles.overlay, open && styles['overlay--open'])}
        aria-label='Закрыть меню'
        onClick={onClose}
        tabIndex={open ? 0 : -1}
      />
      <aside
        ref={drawerRef}
        className={clsx(
          styles.drawer,
          styles[`drawer--${accent}`],
          open && styles['drawer--open'],
        )}
        role='dialog'
        aria-modal={open}
        aria-labelledby={titleId}
        aria-hidden={!open}
        inert={!open}
      >
        <div className={styles.drawerHeader}>
          <h2 id={titleId} className={styles.drawerTitle}>
            {title}
          </h2>
          <button
            type='button'
            className={styles.closeButton}
            onClick={onClose}
            aria-label='Закрыть'
          >
            <X size={20} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className={styles.drawerBody}>{children}</div>
      </aside>
    </>,
    document.body,
  );
};

interface HamburgerButtonProps {
  open?: boolean;
  onClick: () => void;
  label?: string;
}

export const HamburgerButton: React.FC<HamburgerButtonProps> = ({
  open = false,
  onClick,
  label = 'Открыть меню',
}) => (
  <button
    type='button'
    className={styles.hamburger}
    onClick={onClick}
    aria-label={label}
    aria-expanded={open}
  >
    <span
      className={clsx(styles.hamburgerIcon, open && styles['hamburgerIcon--open'])}
      aria-hidden='true'
    >
      <span />
      <span />
      <span />
    </span>
  </button>
);
